import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Heart, MessageCircle, Users, Trophy, Target, Star,
  Gavel, CheckCircle, UserPlus, Crown, Flame,
} from "lucide-react";

const NOTIF_ICONS = {
  like: { icon: Heart, color: "text-pink-500", bg: "bg-pink-50" },
  comment: { icon: MessageCircle, color: "text-blue-500", bg: "bg-blue-50" },
  follow: { icon: UserPlus, color: "text-[#7000FF]", bg: "bg-[#7000FF]/10" },
  user_follow: { icon: UserPlus, color: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10" },
  friend: { icon: Crown, color: "text-pink-600", bg: "bg-pink-50" },
  badge: { icon: Trophy, color: "text-yellow-500", bg: "bg-yellow-50" },
  challenge: { icon: Target, color: "text-emerald-500", bg: "bg-emerald-50" },
  investment: { icon: Star, color: "text-[#7000FF]", bg: "bg-[#7000FF]/10" },
  bid: { icon: Gavel, color: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10" },
  auction_won: { icon: Trophy, color: "text-yellow-500", bg: "bg-yellow-50" },
  auction_outbid: { icon: Flame, color: "text-red-500", bg: "bg-red-50" },
  message: { icon: MessageCircle, color: "text-[#7000FF]", bg: "bg-[#7000FF]/10" },
  credits: { icon: Star, color: "text-[#FF6B35]", bg: "bg-[#FF6B35]/10" },
  report: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50" },
  event: { icon: Users, color: "text-blue-500", bg: "bg-blue-50" },
};

export default function NotificationsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data.notifications || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchNotifications();
    // Mark all as read on mount
    api.post("/notifications/mark-read").catch(() => {});
  }, [fetchNotifications, api]);

  const handleNotifClick = (notif) => {
    const type = notif.type;
    const refId = notif.reference_id;
    const actorId = notif.actor_id;

    if (type === "like" || type === "comment") {
      navigate("/feed");
    } else if (type === "follow" || type === "user_follow" || type === "friend") {
      navigate(`/user/${actorId}`);
    } else if (type === "badge" || type === "investment") {
      if (refId) navigate(`/artist/${refId}`);
    } else if (type === "challenge") {
      navigate("/challenges");
    } else if (type === "bid" || type === "auction_won" || type === "auction_outbid") {
      navigate("/rewards");
    } else if (type === "message") {
      if (refId) navigate(`/messages/${refId}`);
      else navigate("/messages");
    } else if (type === "event") {
      if (refId) navigate(`/events/${refId}`);
    } else if (type === "credits") {
      navigate("/profile");
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "A l'instant";
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}j`;
    return `${Math.floor(days / 7)}sem`;
  };

  return (
    <div className="py-6 space-y-4" data-testid="notifications-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[#FF6B35]" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-xl text-foreground">Notifications</h1>
          <p className="text-xs text-muted-foreground">{notifications.length} notification{notifications.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-2xl h-16 animate-pulse" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-14 h-14 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground font-medium">Aucune notification</p>
          <p className="text-xs text-muted-foreground mt-1">Tes notifications apparaitront ici</p>
        </div>
      ) : (
        <AnimatePresence>
          {notifications.map((notif, index) => {
            const config = NOTIF_ICONS[notif.type] || { icon: Bell, color: "text-muted-foreground", bg: "bg-foreground/5" };
            const IconComp = config.icon;
            return (
              <motion.div
                key={notif.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleNotifClick(notif)}
                className={`bg-card border rounded-2xl p-4 cursor-pointer hover:scale-[1.01] transition-all ${
                  notif.read ? "border-border" : "border-[#FF6B35]/30 shadow-sm"
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center shrink-0`}>
                    <IconComp className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-card-foreground">
                      <span className="font-bold">{typeof notif.actor_name === 'string' ? notif.actor_name : ""}</span>{" "}
                      <span className="text-card-foreground/80">{typeof notif.content === 'string' ? notif.content : ""}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(notif.created_at)}</p>
                  </div>
                  {notif.actor_avatar && (
                    <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
                      <img src={notif.actor_avatar} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {!notif.read && (
                    <div className="w-2 h-2 rounded-full bg-[#FF6B35] shrink-0" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      )}
    </div>
  );
}
